import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import CountryLeadForm from './CountryLeadForm';
import { useEligibilityModal } from '../context/EligibilityModalContext';
import { ALL_STUDY_DESTINATIONS } from '../data/studyDestinationConfigs';

/**
 * Slim, mobile-first eligibility modal.
 * Reuses the same premium `CountryLeadForm` (light variant) that powers
 * the inline forms on country pages and the Home final CTA — so the visual
 * language and lead payload stay consistent across the whole site.
 */
const EligibilityModal = ({ isOpen, onClose }) => {
  const { source } = useEligibilityModal();

  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e) => { if (e.key === 'Escape') onClose(); };
      window.addEventListener('keydown', onKey);
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = original;
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="eligibility-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Check your eligibility"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-[#0B2545]/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-md md:max-w-lg sm:mx-4 sm:my-8 animate-in slide-in-from-bottom sm:zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="eligibility-modal-close"
          className="absolute -top-11 sm:-top-3 right-3 sm:-right-3 w-9 h-9 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
          aria-label="Close"
        >
          <X size={16} className="text-[#1A2B3C]" />
        </button>

        {/* Rounded-top sheet on mobile, full-radius card on desktop */}
        <div className="bg-white rounded-t-3xl sm:rounded-3xl max-h-[88vh] overflow-y-auto shadow-2xl">
          <div className="p-1 sm:p-0">
            <CountryLeadForm
              variant="light"
              source={source || 'patricks-website:eligibility-modal'}
              showCountrySelector
              countryOptions={ALL_STUDY_DESTINATIONS}
              accent="#4DD4AC"
              primary="#2B5B8A"
              dark="#0B2545"
              compact
              headline="Check Your Eligibility"
              subheadline="Fill in your details — a senior counsellor will call you within 24 hours."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EligibilityModal;
